/**
 * Structured withdrawal error handling. The backend answers with RFC 7807 problem details whose `code` is the
 * contract; this module branches on `code` (never on the human message, which is English-only and may change).
 */

export type WithdrawalErrorCode =
  | 'WITHDRAWALS_DISABLED'
  | 'INSUFFICIENT_FUNDS'
  | 'IDEMPOTENCY_CONFLICT'
  | 'INVALID_TRANSITION'
  | 'ACCOUNT_CLOSED'
  | 'FORBIDDEN_TIER'
  | 'METHOD_MISMATCH'
  | 'PAYOUT_DETAILS_MISSING'
  | 'VALIDATION_FAILED';

export interface WithdrawalErrorInfo {
  /** Machine code from the problem body; null when the response carried none (network error, proxy 502, …). */
  code: WithdrawalErrorCode | null;
  status: number | null;
  retryAfterSeconds: number | null;
}

const KNOWN: ReadonlySet<string> = new Set<WithdrawalErrorCode>([
  'WITHDRAWALS_DISABLED',
  'INSUFFICIENT_FUNDS',
  'IDEMPOTENCY_CONFLICT',
  'INVALID_TRANSITION',
  'ACCOUNT_CLOSED',
  'FORBIDDEN_TIER',
  'METHOD_MISMATCH',
  'PAYOUT_DETAILS_MISSING',
  'VALIDATION_FAILED',
]);

const nonNegInt = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;

/** Reads `code` and `retryAfterSeconds` (falling back to the Retry-After header). */
export function parseWithdrawalError(error: unknown): WithdrawalErrorInfo {
  const res = (error as { response?: { status?: number; data?: unknown; headers?: unknown } } | null)?.response;
  const data = (res?.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
  const headers = (res?.headers ?? {}) as Record<string, unknown> & { get?: (name: string) => unknown };
  const headerRetry = Number(headers.get?.('retry-after') ?? headers['retry-after']);
  return {
    code: typeof data.code === 'string' && KNOWN.has(data.code) ? (data.code as WithdrawalErrorCode) : null,
    status: typeof res?.status === 'number' ? res.status : null,
    retryAfterSeconds: nonNegInt(data.retryAfterSeconds) ?? (Number.isFinite(headerRetry) ? nonNegInt(headerRetry) : null),
  };
}
